import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { EventCardComponent } from 'src/app/events/components/event-card/event-card.component';
import { SwiperContainer } from 'swiper/element';

import { SwiperOptions } from 'swiper/types';
import { IconComponent } from '@teamfoster/sdk/icon';
import { CookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { Event } from '../../models';

@Component({
  selector: 'app-event-card-slider',
  templateUrl: './event-card-slider.component.html',
  styleUrl: './event-card-slider.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EventCardComponent, IconComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EventCardSliderComponent implements AfterViewInit, OnDestroy {
  events = input.required<Event[]>();
  maxItems = input<number>(4);
  showNav = input<boolean>(true);
  showArrow = input<boolean>(false);
  priority = input<boolean>(false);
  theme = input<'primary' | 'secondary'>('primary');
  cookiePreferences = input.required<CookiePreferences | undefined | null>();
  swiperIndex$ = signal(0);
  @ViewChild('swiper') swiperEl?: ElementRef<SwiperContainer>;
  cd = inject(ChangeDetectorRef);

  ngAfterViewInit(): void {
    this.initSwiper();
  }

  initSwiper() {
    const that = this;

    const eventCount = this.events()?.length || 0;
    const params: SwiperOptions = {
      //modules: [FreeMode],

      loop: false,
      autoplay: false,
      spaceBetween: 10,
      slidesPerView: 1.5,
      navigation: this.showArrow()
        ? {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }
        : false,
      edgeSwipeDetection: 'prevent', //will prevent system swipe-back navigation
      mousewheel: {
        forceToAxis: true,
      },
      injectStyles: [
        `
        :host .swiper {
          overflow:inherit;
        }
        `,
      ],
      breakpoints: {
        0: {
          slidesPerView: 1.5,
          //slidesOffsetAfter: 100,
        },
        500: {
          slidesPerView: eventCount > 2 ? 'auto' : 2,
        },
        1000: {
          slidesPerView: eventCount > 3 ? 'auto' : this.maxItems() <= 3 ? this.maxItems() : 3,
        },
        1300: {
          slidesPerView: eventCount > 4 ? 'auto' : this.maxItems() <= 4 ? this.maxItems() : 4,
        },
      },
      on: {
        realIndexChange: function (i) {
          that.swiperIndex$.set(i.realIndex);
          that.cd.detectChanges();
        },
        resize: function () {
          that.cd.detectChanges();
        },
      },
    };

    if (this.swiperEl?.nativeElement?.initialize) {
      Object.assign(this.swiperEl.nativeElement, params);
      this.swiperEl.nativeElement.initialize();
      this.cd.detectChanges();
    }
  }

  slideNext() {
    this.swiper?.slideNext();
  }
  slidePrev() {
    this.swiper?.slidePrev();
  }

  get swiper() {
    return this.swiperEl?.nativeElement.swiper;
  }
  get allowSlideNext() {
    return this.swiper?.allowSlideNext;
  }
  get allowSlidePrev() {
    return this.swiper?.allowSlidePrev;
  }
  get isBeginning() {
    return this.swiper?.isBeginning;
  }
  get isEnd() {
    return (this.swiper?.activeIndex || 0) === (this.events()?.length || 0) - 1;
  }

  preventBack(event: any) {
    if (event.wheelDeltaX !== 0) {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    // CRITICAL: Destroy Swiper instance to prevent memory leaks
    this.swiper?.destroy(true, true);
  }
}
